import React from 'react';
import { Badge, Button, Spinner } from 'react-bootstrap';
import { confirmedConstraintIssues, describeReport } from 'Secure/MaskingSources/targetComparison';

const SHOW_AT_MOST = 6;
const qualify = (schema, table) => (schema ? `${schema}.${table}` : table);

/**
 * Bucket a target's report into what the group step will do with it:
 *   shared   — full inventory, joins the one fan-out job
 *   split    — gets its own target_<name> inventory (and job)
 *   pending  — being profiled; cannot be added until the compare re-runs
 *   excluded — nothing left to mask / no schema evidence
 */
export function bucketOf(report) {
  if (!report) return 'shared';
  if (report.skipped) return 'shared';
  if (report.profilePending) return 'pending';
  if (report.snapshotMissing || report.nothingToMask) return 'excluded';
  if (report.hasGaps) return 'split';
  return 'shared';
}

const BADGE = {
  shared: { variant: 'success', label: 'ready' },
  split: { variant: 'warning', label: 'separate' },
  pending: { variant: 'info', label: 'fingerprinting' },
  excluded: { variant: 'danger', label: 'skipped' },
};

function Capped({ items, render }) {
  const shown = items.slice(0, SHOW_AT_MOST);
  return (
    <ul className='mb-0 pl-4' style={{ fontSize: 12 }}>
      {shown.map((i, idx) => (
        <li key={idx}>{render(i)}</li>
      ))}
      {items.length > SHOW_AT_MOST && <li>… and {items.length - SHOW_AT_MOST} more</li>}
    </ul>
  );
}

/**
 * One destination's card: bucket badge + what the compare found (gaps, confirmed duplicate-key
 * conflicts). Without a `report` it shows the in-flight / failed compare state instead, so the
 * card appears the moment the destination is picked and fills in when the server answers.
 */
export function TargetReviewCard({ targetId, name, subtitle, report, comparing, error, onRetry, onRecheck }) {
  // Destination name with its schema (grey, underneath) — the schema is what tells two rows
  // on the same database apart.
  const title = (
    <div style={{ minWidth: 0 }}>
      <strong>{name}</strong>
      {subtitle && (
        <div className='sg-small' style={{ color: 'var(--ink-400)' }} data-testid={`split-review-schema-${targetId}`}>
          {subtitle}
        </div>
      )}
    </div>
  );
  if (!report) {
    return (
      <div
        className='border rounded p-2 mb-2 text-left'
        data-testid={`split-review-row-${targetId}`}
        data-bucket={error ? 'error' : 'comparing'}
      >
        <div className='d-flex align-items-start' style={{ gap: '0.5rem' }}>
          {title}
          {error ? (
            <Badge variant='secondary' data-testid={`split-review-status-${targetId}`}>
              not compared
            </Badge>
          ) : (
            <Badge variant='light' data-testid={`split-review-status-${targetId}`}>
              comparing
            </Badge>
          )}
          {comparing && !error && <Spinner animation='border' size='sm' />}
        </div>
        <div className='sg-small' style={{ color: error ? 'var(--status-danger-text, #B42318)' : 'var(--ink-500)' }}>
          {error ? (
            <>
              Could not compare with the inventory: {error}{' '}
              {onRetry && (
                <Button variant='link' size='sm' className='p-0 align-baseline' onClick={onRetry}>
                  Retry
                </Button>
              )}
            </>
          ) : (
            'Comparing its fingerprinted schema with the inventory…'
          )}
        </div>
      </div>
    );
  }
  const bucket = bucketOf(report);
  const confirmed = confirmedConstraintIssues(report);
  const missingTables = Array.isArray(report.missingTables) ? report.missingTables : [];
  const missingColumns = Array.isArray(report.missingColumns) ? report.missingColumns : [];
  return (
    <div
      className='border rounded p-2 mb-2 text-left'
      data-testid={`split-review-row-${targetId}`}
      data-bucket={bucket}
    >
      <div className='d-flex align-items-start' style={{ gap: '0.5rem' }}>
        {title}
        <Badge variant={BADGE[bucket].variant} data-testid={`split-review-status-${targetId}`}>
          {BADGE[bucket].label}
        </Badge>
        {bucket === 'pending' && onRecheck && (
          <Button
            variant='link'
            size='sm'
            className='p-0 ml-auto'
            style={{ fontSize: 12 }}
            data-testid={`split-review-recheck-${targetId}`}
            onClick={onRecheck}
          >
            Re-check
          </Button>
        )}
      </div>
      <div className='sg-small' style={{ color: 'var(--ink-500)' }}>
        {bucket === 'split' ? (
          <>Inventory name: <code>target_{name}</code></>
        ) : (
          describeReport(report, name)
        )}
      </div>
      {missingTables.length > 0 && (
        <>
          <div className='sg-small mt-1' style={{ fontWeight: 500 }}>Missing tables</div>
          <Capped items={missingTables} render={(m) => <code>{qualify(m.schema, m.table)}</code>} />
        </>
      )}
      {missingColumns.length > 0 && (
        <>
          <div className='sg-small mt-1' style={{ fontWeight: 500 }}>Missing columns</div>
          <Capped
            items={missingColumns}
            render={(m) => (
              <>
                <code>{qualify(m.schema, m.table)}.{m.column}</code>
                {m.algorithm ? `: ${m.algorithm}` : ''}
              </>
            )}
          />
        </>
      )}
      {confirmed.length > 0 && (
        <>
          <div className='sg-small mt-1 text-danger' style={{ fontWeight: 500 }}>
            {confirmed.length === 1 ? '1 column' : `${confirmed.length} columns`} will fail with a duplicate-key
            error on this target
          </div>
          <Capped
            items={confirmed}
            render={(i) => (
              <>
                <code>{qualify(i.schema, i.table)}.{i.column}</code>: {i.algorithm}
              </>
            )}
          />
        </>
      )}
    </div>
  );
}

/**
 * Review of a multi-destination masking step: one card per destination showing whether it
 * shares the fan-out job, is split into its own `target_<name>` inventory (with the tables /
 * columns it lacks), waits on a Profile, or is skipped — plus confirmed duplicate-key conflicts
 * against the TARGET's key constraints. Pure presentation; the host owns confirm/back.
 *
 * `targets` ([{value, label}], optional) fixes the card order to the picker's and lets a
 * destination render BEFORE its report arrives (`comparing` / `compareError` state). Without
 * it the cards follow `reports`. `targetSubtitles` (id → schema / database) renders grey under
 * each destination name.
 */
export default function TargetSplitReview({
  reports,
  targetNames = {},
  targetSubtitles = {},
  targets,
  comparing = false,
  compareError = null,
  onRetry,
  onRecheck,
}) {
  const list = Array.isArray(reports) ? reports.filter(Boolean) : [];
  const byId = new Map(list.map((r) => [r.targetDataSourceId, r]));
  const rows = Array.isArray(targets)
    ? targets.map((t) => ({ id: t.value, name: t.label, report: byId.get(t.value) || null }))
    : list.map((r) => ({ id: r.targetDataSourceId, name: undefined, report: r }));
  const counts = rows.reduce(
    (acc, row) => {
      if (row.report) acc[bucketOf(row.report)] += 1;
      else acc.unknown += 1;
      return acc;
    },
    { shared: 0, split: 0, pending: 0, excluded: 0, unknown: 0 }
  );
  return (
    <div data-testid='split-review' className='text-left'>
      <div className='mb-2' data-testid='split-review-summary'>
        <strong>Review destinations.</strong>{' '}
        {counts.shared > 0 && `${counts.shared} ready, ${counts.shared === 1 ? 'takes' : 'take'} the full inventory in one fan-out job. `}
        {counts.split > 0 &&
          `${counts.split} ${counts.split === 1 ? 'is' : 'are'} missing tables or columns and will run ${
            counts.split === 1 ? 'as a separate job' : 'as separate jobs'
          } with a trimmed target_<name> inventory. `}
        {counts.pending > 0 && `${counts.pending} ${counts.pending === 1 ? 'is' : 'are'} being fingerprinted. Add ${
          counts.pending === 1 ? 'it' : 'them'
        } again once the fingerprint completes. `}
        {counts.excluded > 0 && `${counts.excluded} will be skipped. `}
        {counts.unknown > 0 &&
          (compareError
            ? `${counts.unknown} not compared yet.`
            : `${counts.unknown} still comparing…`)}
      </div>
      {rows.map((row) => (
        <TargetReviewCard
          key={row.id}
          targetId={row.id}
          name={targetNames[row.id] || row.name || (row.report && row.report.targetName) || `#${row.id}`}
          subtitle={targetSubtitles[row.id]}
          report={row.report}
          comparing={comparing}
          error={compareError}
          onRetry={onRetry}
          onRecheck={onRecheck ? () => onRecheck(row.id) : undefined}
        />
      ))}
    </div>
  );
}
